/**
 * Helpdesk custom field definitions from GET /helpdesk/custom-fields
 */
export type HelpdeskCheckboxDisplayStyle = "chips" | "cards";

export interface HelpdeskCustomFieldDefinition {
  key: string;
  name: string;
  description: string | null;
  type: string;
  values_for_dropdown: string[] | null;
  parent_field_key: string | null;
  value_map: Record<string, string[]> | null;
  client_code: string | null;
  show_in_filter: boolean;
  is_required: boolean;
  is_active: boolean;
  /** When `type` is checkbox and options exist: inline chips vs large card row (optional API field). */
  checkbox_display_style?: HelpdeskCheckboxDisplayStyle | null;
}

/** Supported `type` values from the helpdesk admin UI */
export const HELPDESK_CUSTOM_FIELD_TYPES = {
  DROPDOWN_SINGLE: "dropdown_single_select",
  DROPDOWN_MULTI: "dropdown_multi_select",
  TEXT: "text",
  LONG_TEXT: "long_text",
  RADIO: "radio_single_select",
  CHECKBOX: "checkbox",
  NUMBER: "number",
  DATE: "date",
} as const;

/** Map custom field `key` → ticket PUT body key (top-level on ticket) */
export const CUSTOM_FIELD_TO_TICKET_API_KEY: Record<string, string> = {
  "system-name": "client_code",
  "issue-type": "issue_type",
};

export function normalizeHelpdeskFieldType(type: string): string {
  const t = (type || "")?.toLowerCase()?.trim();
  if (t === "dropdown_single_select" || t === "dropdown") {
    return HELPDESK_CUSTOM_FIELD_TYPES.DROPDOWN_SINGLE;
  }
  if (t === "dropdown_multi_select" || t === "multi_select") {
    return HELPDESK_CUSTOM_FIELD_TYPES.DROPDOWN_MULTI;
  }
  if (t === "long_text" || t === "textarea") {
    return HELPDESK_CUSTOM_FIELD_TYPES.LONG_TEXT;
  }
  if (t === "radio" || t === "radio_single_select") {
    return HELPDESK_CUSTOM_FIELD_TYPES.RADIO;
  }
  return t;
}

/**
 * Options for a dropdown / radio field: static list or dependent on parent value via value_map.
 */
export function getEffectiveDropdownOptions(
  field: HelpdeskCustomFieldDefinition,
  valuesByFieldKey: Record<string, unknown>
): string[] {
  if (field?.parent_field_key && field?.value_map) {
    const parentVal = valuesByFieldKey?.[field?.parent_field_key];
    const parentStr =
      typeof parentVal === "string" ? parentVal : String(parentVal ?? "");
    if (!parentStr) return [];
    return field?.value_map?.[parentStr] ?? [];
  }
  return field?.values_for_dropdown ?? [];
}

export function isDropdownLikeField(
  field: HelpdeskCustomFieldDefinition
): boolean {
  const t = normalizeHelpdeskFieldType(field?.type);
  return (
    t === HELPDESK_CUSTOM_FIELD_TYPES.DROPDOWN_SINGLE ||
    t === HELPDESK_CUSTOM_FIELD_TYPES.DROPDOWN_MULTI ||
    t === HELPDESK_CUSTOM_FIELD_TYPES.RADIO
  );
}

/** Hierarchical (depth-first): each child renders immediately after its parent. */
export function orderCustomFieldsForRender(
  fields: HelpdeskCustomFieldDefinition[] | undefined
): HelpdeskCustomFieldDefinition[] {
  if (!fields?.length) return [];
  const active = fields?.filter((f) => f?.is_active) ?? [];

  const childrenByParent = new Map<string, HelpdeskCustomFieldDefinition[]>();
  for (const field of active) {
    const parentKey = field?.parent_field_key;
    if (!parentKey) continue;
    const list = childrenByParent.get(parentKey) ?? [];
    list.push(field);
    childrenByParent.set(parentKey, list);
  }

  const ordered: HelpdeskCustomFieldDefinition[] = [];
  const visited = new Set<string>();

  const visit = (field: HelpdeskCustomFieldDefinition) => {
    if (!field?.key || visited.has(field.key)) return;
    visited.add(field.key);
    ordered.push(field);
    const children = childrenByParent.get(field.key) ?? [];
    for (const child of children) visit(child);
  };

  const roots = active.filter((f) => !f?.parent_field_key);
  for (const root of roots) visit(root);

  for (const field of active) {
    if (!visited.has(field?.key)) {
      visited.add(field?.key);
      ordered.push(field);
    }
  }

  return ordered;
}

export function getChildFieldKeys(
  parentKey: string,
  fields: HelpdeskCustomFieldDefinition[]
): string[] {
  return fields
    ?.filter((f) => f?.parent_field_key === parentKey)
    ?.map((f) => f?.key);
}

/** `parent_field_key` on the definition for `fieldKey`, if any (dependent child → parent). */
export function getParentFieldKeyForField(
  fieldKey: string,
  fields: HelpdeskCustomFieldDefinition[] | undefined
): string | undefined {
  const def = fields?.find((f) => f?.key === fieldKey);
  const p = def?.parent_field_key;
  return typeof p === "string" && p?.trim() !== "" ? p : undefined;
}

export function getDefaultValueForFieldType(
  type: string,
  valuesForDropdown?: string[] | null
): unknown {
  const t = normalizeHelpdeskFieldType(type);
  switch (t) {
    case HELPDESK_CUSTOM_FIELD_TYPES.DROPDOWN_MULTI:
      return [];
    case HELPDESK_CUSTOM_FIELD_TYPES.CHECKBOX:
      if (valuesForDropdown?.length) return [];
      return false;
    case HELPDESK_CUSTOM_FIELD_TYPES.NUMBER:
      return "";
    case HELPDESK_CUSTOM_FIELD_TYPES.DATE:
      return null;
    default:
      return "";
  }
}

function readTicketCustomFieldIfPresent(
  ticket: { custom_fields?: Record<string, unknown> | null },
  key: string
): { found: false } | { found: true; value: unknown } {
  const cf = ticket.custom_fields;
  if (!cf || !Object.prototype.hasOwnProperty.call(cf, key)) {
    return { found: false };
  }
  return { found: true, value: cf[key] };
}

/**
 * Form values for header dynamic fields: uses GET ticket `custom_fields` first
 * (e.g. `{ "system-name": "Hubspot" }`), then top-level `client_code` /
 * `issue_type` for legacy mapped fields when the key is absent from `custom_fields`.
 */
export function buildCustomFieldsValuesFromTicket(
  ticket: {
    client_code?: string;
    issue_type?: string | null;
    custom_fields?: Record<string, unknown> | null;
  },
  definitions: HelpdeskCustomFieldDefinition[] | undefined
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (!definitions?.length) return out;
  const cf = ticket.custom_fields ?? undefined;
  for (const f of definitions) {
    if (!f?.is_active) continue;
    const apiKey = CUSTOM_FIELD_TO_TICKET_API_KEY[f?.key];
    if (apiKey === "client_code") {
      const fromCf = readTicketCustomFieldIfPresent(ticket, f.key);
      if (fromCf.found) {
        out[f.key] = fromCf.value == null ? "" : fromCf.value;
      } else {
        out[f.key] = ticket?.client_code ?? "";
      }
    } else if (apiKey === "issue_type") {
      const fromCf = readTicketCustomFieldIfPresent(ticket, f.key);
      if (fromCf.found) {
        out[f.key] = fromCf.value ?? null;
      } else {
        out[f.key] = ticket?.issue_type ?? null;
      }
    } else {
      const raw = cf?.[f?.key];
      out[f?.key] =
        raw !== undefined && raw !== null
          ? raw
          : getDefaultValueForFieldType(f?.type, f?.values_for_dropdown);
    }
  }
  return out;
}

/** True if this value should not appear on the wire (omit the key). */
export function shouldOmitCustomFieldFromPayload(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string" && value?.trim() === "") return true;
  if (Array.isArray(value) && value?.length === 0) return true;
  if (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    !(value instanceof Date) &&
    Object.keys(value as object).length === 0
  ) {
    return true;
  }
  return false;
}

export type CustomFieldApiValue = string | number | boolean | string[] | null;

/** Coerce defined custom fields before omitting empties (see {@link serializeTicketCustomFieldsPayload}). */
export function coerceHelpdeskCustomFieldValueForApi(
  value: unknown,
  rawType: string,
  valuesForDropdown?: string[] | null
): CustomFieldApiValue {
  const t = normalizeHelpdeskFieldType(rawType);
  switch (t) {
    case HELPDESK_CUSTOM_FIELD_TYPES.TEXT:
    case HELPDESK_CUSTOM_FIELD_TYPES.LONG_TEXT:
      return value == null ? "" : String(value);
    case HELPDESK_CUSTOM_FIELD_TYPES.DATE:
      if (value == null || value === "") return "";
      if (value instanceof Date) return value?.toISOString();
      return String(value);
    case HELPDESK_CUSTOM_FIELD_TYPES.NUMBER: {
      if (value === "" || value == null) return "";
      if (typeof value === "number" && Number.isFinite(value)) return value;
      const n = Number(String(value).trim());
      return Number.isFinite(n) ? n : "";
    }
    case HELPDESK_CUSTOM_FIELD_TYPES.DROPDOWN_SINGLE:
    case HELPDESK_CUSTOM_FIELD_TYPES.RADIO:
      if (value == null || value === "") return "";
      return String(value);
    case HELPDESK_CUSTOM_FIELD_TYPES.DROPDOWN_MULTI:
      if (!Array.isArray(value)) {
        if (value == null || value === "") return [];
        return [String(value)];
      }
      return value?.filter((v) => v != null).map((v) => String(v));
    case HELPDESK_CUSTOM_FIELD_TYPES.CHECKBOX:
      if (valuesForDropdown?.length) {
        if (!Array.isArray(value)) {
          if (value == null || value === "") return [];
          return [String(value)];
        }
        return value?.filter((v) => v != null).map((v) => String(v));
      }
      return Boolean(value);
    default:
      if (value == null) return "";
      return String(value);
  }
}

/**
 * Build `custom_fields` for the API: coerce by active definitions, then drop any
 * key whose value is empty (`""`, `[]`, null, undefined, empty plain object).
 */
export function serializeTicketCustomFieldsPayload(
  merged: Record<string, unknown>,
  definitions: HelpdeskCustomFieldDefinition[] | undefined
): Record<string, CustomFieldApiValue> {
  const out: Record<string, CustomFieldApiValue> = {};
  const activeByKey = new Map(
    (definitions ?? []).filter((f) => f?.is_active).map((f) => [f.key, f])
  );
  for (const key of Object.keys(merged)) {
    const def = activeByKey?.get(key);
    if (!def) continue;
    const coerced = coerceHelpdeskCustomFieldValueForApi(
      merged[key],
      def.type,
      def.values_for_dropdown
    );
    if (!shouldOmitCustomFieldFromPayload(coerced)) {
      out[key] = coerced;
    }
  }
  return out;
}
